import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import Navbar from "./Navbar";
import "./CityLeaderboard.css";

// =====================================================
// LIVE CITY LEADERBOARD — "TAKE YOUR CITY FIRST"
//
// Two INDEPENDENT boards, side by side on a desktop and stacked on a phone:
// ❤️ Love and 🤝 Friendship. Their counts are never mixed and never summed —
// a city can lead one board and sit mid-table on the other, which is the whole
// point of the competition.
//
// WHERE THE NUMBERS COME FROM
//   GET /api/love/paid/cities does the grouping, the sorting and the limiting
//   in MongoDB ($facet → two boards in one pass). This page renders the arrays
//   in the order they arrive: no client-side tallying, no re-sorting, and one
//   request for both boards.
// =====================================================

const BASE_URL =
  window.location.hostname === "localhost"
    ? "http://localhost:5000"
    : "https://success-blog-app-backend.onrender.com";

const CITIES_URL = `${BASE_URL}/api/love/paid/cities`;

// Matches the Live Leaderboard's own refresh cadence.
const REFRESH_MS = 60 * 1000;

const api = axios.create({ timeout: 12000 });

const RANK_ICONS = ["🥇", "🥈", "🥉"];

/* =========================================================
   ONE CITY ROW
   The bar is scaled against the LEADER of its own board, so the two boards
   stay visually independent even when their totals differ wildly.
========================================================= */

const CityRow = memo(function CityRow({ row, index, leaderCount, tone }) {
  const pct = leaderCount > 0 ? Math.max(6, Math.round((row.count / leaderCount) * 100)) : 0;

  return (
    <li
      className={`cl-row ${index < 3 ? `cl-row-top cl-row-${index + 1}` : ""}`}
      style={{ animationDelay: `${Math.min(index, 12) * 40}ms` }}
    >
      <span className="cl-rank">{RANK_ICONS[index] || `#${index + 1}`}</span>

      {/* City only. One main city per state, so "Bengaluru" already says
          Karnataka to everyone reading the board. */}
      <span className="cl-city">
        <span className="cl-city-name">{row.city}</span>
      </span>

      <span className={`cl-count cl-count-${tone}`}>
        {tone === "love" ? "❤️" : "🤝"}
        {row.count}
      </span>

      <span className="cl-bar" aria-hidden="true">
        <span className={`cl-bar-fill cl-bar-${tone}`} style={{ width: `${pct}%` }} />
      </span>
    </li>
  );
});

/* =========================================================
   ONE BOARD (Love or Friendship)
========================================================= */

const CityBoard = memo(function CityBoard({ tone, title, rows, loading }) {
  const leaderCount = rows.length ? rows[0].count : 0;

  return (
    <section className={`cl-board cl-board-${tone}`}>
      <header className="cl-board-head">
        <span className="cl-board-icon" aria-hidden="true">{tone === "love" ? "❤️" : "🤝"}</span>
        <h2 className="cl-board-title">{title}</h2>
        {!loading && rows.length > 0 && (
          <span className="cl-board-count">{rows.length} {rows.length === 1 ? "city" : "cities"}</span>
        )}
      </header>

      {loading ? (
        <ul className="cl-list" aria-hidden="true">
          {[0, 1, 2, 3, 4].map((i) => (
            <li key={i} className="cl-row cl-row-skeleton">
              <span className="cl-skeleton" />
            </li>
          ))}
        </ul>
      ) : rows.length === 0 ? (
        // EMPTY STATE — no city has an entry on this board yet. It is an
        // invitation, not an error.
        <div className="cl-empty">
          <div className="cl-empty-icon">{tone === "love" ? "💌" : "🫂"}</div>
          <p className="cl-empty-title">No city on the board yet</p>
          <p className="cl-empty-sub">
            Be the first — your entry puts your city at #1.
          </p>
        </div>
      ) : (
        <ol className="cl-list">
          {rows.map((row, index) => (
            <CityRow
              key={`${row.state}-${row.city}`}
              row={row}
              index={index}
              leaderCount={leaderCount}
              tone={tone}
            />
          ))}
        </ol>
      )}
    </section>
  );
});

/* =========================================================
   PAGE
========================================================= */

export default function CityLeaderboard() {
  const navigate = useNavigate();

  const [boards, setBoards] = useState({ love: [], friend: [] });
  const [totals, setTotals] = useState({ love: 0, friend: 0, cities: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchCities = useCallback(async () => {
    try {
      const res = await api.get(CITIES_URL);

      // The server already sorted both boards; the arrays are used as they are.
      setBoards({
        love: Array.isArray(res.data?.love) ? res.data.love : [],
        friend: Array.isArray(res.data?.friend) ? res.data.friend : [],
      });
      setTotals({
        love: res.data?.totals?.love || 0,
        friend: res.data?.totals?.friend || 0,
        cities: res.data?.totals?.cities || 0,
      });
      setError("");
    } catch (err) {
      // A failed refresh must not wipe the standings already on screen.
      setError("Could not update the city standings. Retrying…");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCities();
  }, [fetchCities]);

  // Live standings, with the same 60s cadence as the Live Leaderboard. A
  // backgrounded tab does not poll: nobody is looking at it, and the next
  // refresh happens as soon as it is.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === "visible") fetchCities();
    }, REFRESH_MS);

    return () => clearInterval(id);
  }, [fetchCities]);

  const leaders = useMemo(
    () => ({
      love: boards.love[0] || null,
      friend: boards.friend[0] || null,
    }),
    [boards]
  );

  return (
    <div className="cl-wrapper">
      <div className="cl-aurora" aria-hidden="true">
        <span className="cl-aurora-a" />
        <span className="cl-aurora-b" />
        <span className="cl-aurora-c" />
      </div>

      <Navbar />

      <main className="cl-page">
        <section className="cl-hero">
          <div className="cl-hero-badge">
            <span className="cl-hero-dot" />
            Live City Leaderboard
            <span className="cl-hero-dot" />
          </div>

          <h1 className="cl-hero-title">TAKE YOUR CITY FIRST</h1>

          <p className="cl-hero-sub">Let's make our city #1.</p>

          <p className="cl-hero-note">
            Every name added carries its city up the board. Love and Friendship
            are counted separately — win both.
          </p>

          {!loading && (
            <div className="cl-hero-stats">
              <span className="cl-stat">
                <strong>{totals.cities}</strong> cities competing
              </span>
              <span className="cl-stat cl-stat-love">
                ❤️ <strong>{totals.love}</strong> love entries
              </span>
              <span className="cl-stat cl-stat-friend">
                🤝 <strong>{totals.friend}</strong> friendship entries
              </span>
            </div>
          )}

          {(leaders.love || leaders.friend) && (
            <p className="cl-hero-leaders">
              {leaders.love && <>❤️ <strong>{leaders.love.city}</strong> leads Love</>}
              {leaders.love && leaders.friend && <span className="cl-hero-sep"> · </span>}
              {leaders.friend && <>🤝 <strong>{leaders.friend.city}</strong> leads Friendship</>}
            </p>
          )}

          <button type="button" className="cl-cta" onClick={() => navigate("/loveadd")}>
            ✍️ Add your name for your city
          </button>

          {error && <p className="cl-error">{error}</p>}
        </section>

        {/* Side by side on a desktop, stacked on a phone. */}
        <div className="cl-boards">
          <CityBoard tone="love" title="Love Leaderboard" rows={boards.love} loading={loading} />
          <CityBoard tone="friend" title="Friendship Leaderboard" rows={boards.friend} loading={loading} />
        </div>

        <p className="cl-footnote">
          Standings count every verified entry ever added for a city. Updates
          automatically every minute.
        </p>

        <button type="button" className="cl-back" onClick={() => navigate("/lovelist")}>
          ← Back to Live Leaderboard
        </button>
      </main>
    </div>
  );
}
